import {
    signIn,
    signUp,
    signOut,
    resetPassword,
    confirmResetPassword,
    fetchAuthSession,
    getCurrentUser,
    fetchUserAttributes,
    confirmSignIn,
    signInWithRedirect,
    confirmSignUp,
    resendSignUpCode,
    type SignUpInput,
    type ResetPasswordInput,
    type ConfirmResetPasswordInput,
} from '@aws-amplify/auth';

import type { AuthResponse, BackendUser, LoginPayload, RegisterPayload } from '../types/auth.types';

class AmplifyAuthService {

    async login(payload: LoginPayload): Promise<AuthResponse> {
        try {
            try {
                const existingSession = await fetchAuthSession();
                if (existingSession.tokens?.accessToken) {
                    await signOut();
                }
            } catch {
                // Ignore session check errors
            }

            const signInOutput = await signIn({
                username: payload.username,
                password: payload.password,
            });

            if (signInOutput.isSignedIn) {
                const session = await fetchAuthSession();
                const user = await getCurrentUser();
                const attributes = await fetchUserAttributes();

                const userData: BackendUser = {
                    id: user.userId,
                    first_name: attributes.given_name || attributes.email?.split('@')[0] || '',
                    last_name: attributes.family_name || '',
                    email: attributes.email || payload.username,
                    user_type: attributes['custom:user_type'] || 'USER',
                    is_email_verified: attributes.email_verified === 'true' || (typeof attributes.email_verified === 'boolean' ? attributes.email_verified : false),
                    auth_provider: 'cognito',
                    ...(attributes.phone_number && { phone_number: attributes.phone_number }),
                    ...(attributes.birthdate && { dob: attributes.birthdate }),
                    ...(attributes.gender && { gender: attributes.gender }),
                };

                return {
                    token: session.tokens?.accessToken?.toString() || '',
                    user: userData,
                    access_token: session.tokens?.accessToken?.toString() || '',
                    id_token: session.tokens?.idToken?.toString() || '',
                };
            }

            if (signInOutput.nextStep) {
                const stepType = signInOutput.nextStep.signInStep;

                if (stepType === 'CONFIRM_SIGN_UP') {
                    throw new Error('Please verify your email before signing in. Check your inbox for the verification code.');
                }

                throw new Error(`Additional step required: ${stepType}`);
            }

            throw new Error('Sign in failed');
        } catch (error: unknown) {
            const err = error as Error;
            throw this.handleAmplifyError(err);
        }
    }

    async register(payload: RegisterPayload): Promise<AuthResponse> {
        try {
            const signUpInput: SignUpInput = {
                username: payload.email,
                password: payload.password,
                options: {
                    userAttributes: {
                        email: payload.email,
                        given_name: payload.first_name,
                        family_name: payload.last_name,
                        ...(payload.dob && payload.dob !== "2000-01-01" && { birthdate: payload.dob }),
                        ...(payload.gender && payload.gender !== "Prefer not to say" && { gender: payload.gender }),
                        ...(payload.phone_number && { phone_number: payload.phone_number }),
                    },
                    autoSignIn: false,
                },
            };

            await signUp(signUpInput);

            return {
                token: '',
                user: {
                    first_name: payload.first_name,
                    last_name: payload.last_name,
                    email: payload.email,
                    user_type: 'USER',
                    is_email_verified: false,
                    auth_provider: 'cognito',
                },
            };
        } catch (error: unknown) {
            const err = error as Error;
            throw this.handleAmplifyError(err);
        }
    }

    async verifyEmail(email: string, code: string): Promise<{ message: string }> {
        try {
            const confirmOutput = await confirmSignUp({
                username: email,
                confirmationCode: code,
            });

            if (confirmOutput.isSignUpComplete) {
                return { message: 'Email verified successfully. You can now sign in.' };
            }

            throw new Error('Email verification incomplete');
        } catch (error: unknown) {
            const err = error as Error;
            throw this.handleAmplifyError(err);
        }
    }

    async sendEmailVerification(email?: string): Promise<{ message: string }> {
        try {
            let userEmail = email;

            if (!userEmail) {
                const tempUserData = localStorage.getItem('temp_user_data');
                if (tempUserData) {
                    const userData = JSON.parse(tempUserData);
                    userEmail = userData.email;
                }
            }

            if (!userEmail) {
                throw new Error('Email address is required');
            }

            await resendSignUpCode({
                username: userEmail,
            });

            return { message: 'Verification email sent successfully.' };
        } catch (error: unknown) {
            const err = error as Error;
            throw this.handleAmplifyError(err);
        }
    }

    async requestPasswordReset(email: string): Promise<{ message: string }> {
        try {
            const resetInput: ResetPasswordInput = {
                username: email,
            };

            await resetPassword(resetInput);

            return { message: 'Password reset code sent to your email.' };
        } catch (error: unknown) {
            const err = error as Error & { name?: string };

            if (err.name === 'UserNotFoundException') {
                throw new Error('No account found with this email address');
            } else if (err.name === 'LimitExceededException') {
                throw new Error('Too many requests. Please wait before trying again');
            }

            throw this.handleAmplifyError(err);
        }
    }

    async resetPassword(email: string, code: string, newPassword: string): Promise<{ message: string }> {
        try {
            const confirmResetInput: ConfirmResetPasswordInput = {
                username: email,
                confirmationCode: code,
                newPassword,
            };

            await confirmResetPassword(confirmResetInput);
            return { message: 'Password reset successfully. You can now sign in.' };
        } catch (error: unknown) {
            const err = error as Error;
            throw this.handleAmplifyError(err);
        }
    }

    async setNewPassword(temporaryPassword: string, newPassword: string): Promise<{ message: string }> {
        try {
            const storedChallenge = localStorage.getItem('amplify_signin_result');
            if (!storedChallenge) {
                throw new Error('No pending password challenge found. Please sign in first.');
            }

            const challengeInfo = JSON.parse(storedChallenge);
            const signInResult = await signIn({
                username: challengeInfo.username,
                password: temporaryPassword,
            });

            if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                await confirmSignIn({
                    challengeResponse: newPassword,
                });

                localStorage.removeItem('amplify_signin_result');
                return { message: 'Password set successfully. You can now sign in.' };
            }

            throw new Error('No new password challenge found.');
        } catch (error: unknown) {
            const err = error as Error;
            throw this.handleAmplifyError(err);
        }
    }

    async signInWithGoogle(): Promise<void> {
        try {
            try {
                const existingSession = await fetchAuthSession();
                if (existingSession.tokens?.accessToken) {
                    await signOut();
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            } catch {
                // Ignore session check errors
            }

            await signInWithRedirect({ provider: 'Google' });
        } catch (error: unknown) {
            const err = error as Error;
            throw this.handleAmplifyError(err);
        }
    }

    async handleOAuthCallback(): Promise<AuthResponse | null> {
        try {
            const session = await fetchAuthSession();

            if (session.tokens?.accessToken && session.tokens?.idToken) {
                const attributes = await fetchUserAttributes();
                const currentUser = await getCurrentUser();

                const user: BackendUser = {
                    id: currentUser.userId,
                    first_name: attributes.given_name || attributes.email?.split('@')[0] || '',
                    last_name: attributes.family_name || '',
                    email: attributes.email || '',
                    user_type: attributes['custom:user_type'] || 'USER',
                    is_email_verified: attributes.email_verified === 'true' || (typeof attributes.email_verified === 'boolean' ? attributes.email_verified : false),
                    auth_provider: 'google',
                    ...(attributes.phone_number && { phone_number: attributes.phone_number }),
                    ...(attributes.birthdate && { dob: attributes.birthdate }),
                    ...(attributes.gender && { gender: attributes.gender }),
                    ...(attributes.picture && { avatar: attributes.picture }),
                };

                return {
                    token: session.tokens.accessToken.toString(),
                    user,
                    access_token: session.tokens.accessToken.toString(),
                    id_token: session.tokens.idToken.toString(),
                };
            }

            return null;
        } catch (error: unknown) {
            const err = error as Error;
            throw this.handleAmplifyError(err);
        }
    }

    async logout(): Promise<{ message: string }> {
        try {
            await signOut();
            return { message: 'Logged out successfully' };
        } catch (error: unknown) {
            const err = error as Error;
            throw this.handleAmplifyError(err);
        }
    }

    async getProfile(): Promise<BackendUser> {
        try {
            const currentUser = await getCurrentUser();
            const attributes = await fetchUserAttributes();

            return {
                id: currentUser.userId,
                first_name: attributes.given_name || attributes.email?.split('@')[0] || '',
                last_name: attributes.family_name || '',
                email: attributes.email || '',
                user_type: attributes['custom:user_type'] || 'USER',
                is_email_verified: attributes.email_verified === 'true' || (typeof attributes.email_verified === 'boolean' ? attributes.email_verified : false),
                auth_provider: 'cognito',
                ...(attributes.phone_number && { phone_number: attributes.phone_number }),
                ...(attributes.birthdate && { dob: attributes.birthdate }),
                ...(attributes.gender && { gender: attributes.gender }),
            };
        } catch (error: unknown) {
            const err = error as Error;
            throw this.handleAmplifyError(err);
        }
    }

    async checkAuthStatus(): Promise<{
        isAuthenticated: boolean;
        user?: BackendUser;
        tokens?: {
            accessToken: string;
            idToken: string;
        };
    }> {
        try {
            const session = await fetchAuthSession();

            if (session.tokens?.accessToken && session.tokens?.idToken) {
                try {
                    const user = await this.getProfile();
                    return {
                        isAuthenticated: true,
                        user,
                        tokens: {
                            accessToken: session.tokens.accessToken.toString(),
                            idToken: session.tokens.idToken.toString(),
                        },
                    };
                } catch {
                    return { isAuthenticated: false };
                }
            }

            return { isAuthenticated: false };
        } catch {
            return { isAuthenticated: false };
        }
    }

    private handleAmplifyError(error: Error & { name?: string }): Error {
        const errorMessage = error.message || error.toString();

        if (errorMessage.includes('NotAuthorizedException')) {
            return new Error('Invalid email or password. Please check your credentials.');
        }

        if (errorMessage.includes('UserNotFoundException')) {
            return new Error('No account found with this email. Please register first.');
        }

        if (errorMessage.includes('UserNotConfirmedException') || errorMessage.includes('EMAIL_NOT_VERIFIED')) {
            return new Error('Please verify your email before signing in. Check your inbox for the verification code.');
        }

        if (errorMessage.includes('UsernameExistsException') || errorMessage.includes('User already exists')) {
            return new Error('This email is already registered. If you haven\'t verified your email yet, please check your inbox for the verification code and verify your account, then try logging in.');
        }

        if (errorMessage.includes('custom:user_type') || errorMessage.includes('InvalidParameterException')) {
            return new Error('Registration temporarily unavailable due to system configuration. Please try again later or contact support.');
        }

        if (errorMessage.includes('InvalidPasswordException')) {
            return new Error('Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character.');
        }

        if (errorMessage.includes('CodeMismatchException')) {
            return new Error('Invalid verification code. Please check the code and try again.');
        }

        if (errorMessage.includes('ExpiredCodeException')) {
            return new Error('Verification code has expired. Please request a new code.');
        }

        if (errorMessage.includes('LimitExceededException')) {
            return new Error('Too many attempts. Please try again later.');
        }

        if (errorMessage.includes('TooManyRequestsException')) {
            return new Error('Too many requests. Please wait a moment and try again.');
        }

        if (errorMessage.includes('There is already a signed in user') || errorMessage.includes('already a signed in user')) {
            return new Error('Another user is already signed in. Please sign out first or refresh the page.');
        }

        return new Error(errorMessage);
    }
}

export const amplifyAuthService = new AmplifyAuthService();
export default AmplifyAuthService;
